import { BaseViewStore } from 'core/stores';
import { action, computed, makeObservable, observable } from 'mobx';
import { CarrierServiceStore } from 'modules/carrier/stores';
import { notificationError } from 'core/utils';
import _ from 'lodash';

class ChannelExcludedCarrierServicesViewStore extends BaseViewStore {
	@observable carrierServiceGroups = [];
	@observable hasNoRequiredCarriers = false;

	// Excluded ids that are not rendered. Carried through submit unchanged so a partial fetch can never
	// silently drop stored exclusions, and so the ones ShipStation does not quote survive opening this modal
	// and saving the channel. Exclusions left behind by a carrier type that is no longer required are pruned
	// by ChannelEditViewStore.onRequiredCarriersChange instead.
	unmatchedExcludedIds = [];

	constructor(rootStore, { modalParams }) {
		super(rootStore);
		makeObservable(this);
		this.modalParams = modalParams;
		this.parentViewStore = modalParams.data;
		this.carrierServiceStore = new CarrierServiceStore(rootStore);
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		// Which carrier services the channel can use is the parent's rule to state - a service is only reachable
		// if the channel requires a carrier of its carrier type, or, on a ShipStation channel, if ShipStation
		// quotes it. Only those groups are worth showing.
		const { carrierTypeIds, hasNoRequiredCarriers } = this.parentViewStore.carrierServiceScope;
		if (hasNoRequiredCarriers) {
			this.hasNoRequiredCarriers = true;
			return;
		}

		this.suspend();
		try {
			const filter = {
				page: 1,
				rpp: 500,
				orderBy: 'name',
				orderDirection: 'asc',
				embed: ['carrierType'],
			};
			if (carrierTypeIds.length > 0) {
				filter.carrierTypeIds = carrierTypeIds;
			}
			await this.carrierServiceStore.find(filter);

			// The completeness check is against what was fetched, before the in-scope filter drops anything.
			const fetchedCarrierServices = this.carrierServiceStore.data;
			if (_.get(this.carrierServiceStore, 'response.totalRecords', 0) > fetchedCarrierServices.length) {
				this.rootStore.notificationStore.error(['EXCLUDEDCARRIERSERVICES.INCOMPLETELIST']);
			}
			const carrierServices = _.filter(fetchedCarrierServices, carrierService =>
				this.parentViewStore.isCarrierServiceInScope(carrierService)
			);

			const excludedIds = _.map(this.parentViewStore.excludedCarrierServiceIds, id => String(id));
			const renderedIds = _.map(carrierServices, carrierService => String(carrierService.id));
			this.unmatchedExcludedIds = _.filter(excludedIds, id => !_.includes(renderedIds, id));

			this.carrierServiceGroups = _.sortBy(
				_.map(
					_.groupBy(carrierServices, carrierService => _.get(carrierService, 'carrierType.name') || '-'),
					(items, carrierTypeName) => ({
						carrierTypeName,
						// Carrier type names contain spaces ("Amazon Shipping") and the checkbox id ends up in
						// htmlFor and data-cy, so it has to be slugified.
						carrierTypeSlug: _.kebabCase(carrierTypeName) || 'other',
						items: _.map(items, carrierService => ({
							id: carrierService.id,
							name: carrierService.name,
							isChecked: !_.includes(excludedIds, String(carrierService.id)),
						})),
					})
				),
				'carrierTypeName'
			);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	onCheck = item => {
		item.isChecked = !item.isChecked;
	};

	@action.bound
	onGroupSelectAllCheck = group => {
		const allChecked = this.isGroupSelectAllChecked(group);
		_.forEach(group.items, item => {
			item.isChecked = !allChecked;
		});
	};

	@action.bound
	onSelectAllCheck = () => {
		const allChecked = this.isSelectAllChecked;
		_.forEach(this.allItems, item => {
			item.isChecked = !allChecked;
		});
	};

	// Drives the hint text: a ShipStation channel lists what ShipStation quotes, not the required carriers'
	// services.
	@computed get isShipStationChannel() {
		return this.parentViewStore.isShipStationChannel;
	}

	// Not a computed because computeds cannot take arguments. Reading the observables here still tracks
	// correctly inside an observer render.
	isGroupSelectAllChecked = group => group.items.length > 0 && group.items.every(item => item.isChecked);

	@computed get allItems() {
		return _.flatMap(this.carrierServiceGroups, group => group.items);
	}

	@computed get isSelectAllChecked() {
		return this.allItems.length > 0 && this.allItems.every(item => item.isChecked);
	}

	@computed get excludedCount() {
		return _.filter(this.allItems, item => !item.isChecked).length;
	}

	@computed get allowedCount() {
		return this.allItems.length - this.excludedCount;
	}

	@action.bound
	submit = () => {
		const excludedIds = [
			...this.unmatchedExcludedIds,
			..._.map(_.filter(this.allItems, item => !item.isChecked), item => item.id),
		];
		this.parentViewStore.setExcludedCarrierServiceIds(excludedIds);
		this.modalParams.close();
	};
}

export default ChannelExcludedCarrierServicesViewStore;
